// Quality — Data quality scorecard (live API)
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { QualityRule, QualityScore } from "@/lib/types";

type Violation = { recordId: string; domainId: string; ruleId: string; ruleName: string; severity: string; evaluatedAt: string };

const DOMAINS = ["instruments", "counterparties", "currencies", "countries"];

export function Quality() {
  const [rules, setRules] = useState<QualityRule[]>([]);
  const [scores, setScores] = useState<Record<string, QualityScore>>({});
  const [violations, setViolations] = useState<Violation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRule, setNewRule] = useState<{ domainId: string; name: string; expression: string; severity: "info" | "warn" | "error" }>(
    { domainId: "instruments", name: "", expression: "", severity: "warn" },
  );
  const toast = useToast();

  async function loadAll() {
    setLoading(true); setError(null);
    try {
      const [r, v, ...sc] = await Promise.all([
        api.quality.rules(),
        api.quality.violations({ limit: 25 }),
        ...DOMAINS.map(d => api.quality.score(d).catch(() => null)),
      ]);
      setRules(r);
      setViolations(v);
      const sMap: Record<string, QualityScore> = {};
      DOMAINS.forEach((d, i) => { if (sc[i]) sMap[d] = sc[i] as QualityScore; });
      setScores(sMap);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load quality data");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); }, []);

  const kpis = useMemo(() => {
    const list = DOMAINS.map(d => {
      const s = scores[d];
      const total = s?.totalRecords ?? 0;
      const passing = s?.passing ?? 0;
      const score = total ? Math.round((passing / Math.max(1, passing + (s?.failing ?? 0))) * 100) : 0;
      return { name: d, score, trend: s?.trend ?? [] };
    });
    return list;
  }, [scores]);

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await api.quality.runAll();
      toast(`Ran ${r.rules} rules · ${r.pass} pass / ${r.fail} fail`, "ok");
      await loadAll();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Run failed", "error");
    } finally {
      setRunning(false);
    }
  };

  const toggleRule = async (rule: QualityRule, enabled: boolean) => {
    try {
      await api.quality.updateRule(rule.id, { enabled });
      toast(`${rule.name} ${enabled ? "enabled" : "disabled"}`, "ok");
      await loadAll();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const submitNewRule = async () => {
    try {
      await api.quality.createRule(newRule);
      toast(`Rule "${newRule.name}" created`, "ok");
      setCreating(false);
      setNewRule({ domainId: "instruments", name: "", expression: "", severity: "warn" });
      await loadAll();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Create failed", "error");
    }
  };

  if (loading) return <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>Loading quality data…</div>;
  if (error) return <div style={{ padding: 24, color: "var(--md-error)" }}>{error}</div>;

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <header style={{ ...panel, padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={eyebrow}>HEALTH</div>
          <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400 }}>Quality scorecard</div>
        </div>
        <button onClick={runAll} disabled={running} style={filledBtn}>
          {running ? "Running…" : "Run all rules"}
        </button>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {kpis.map(k => (
          <div key={k.name} style={{ ...panel, padding: 18 }}>
            <div style={eyebrow}>{k.name.toUpperCase()}</div>
            <div style={{ font: "var(--t-display-sm)", marginTop: 6 }} className="tabular">
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>{k.score}</span>
              <span style={{ font: "var(--t-headline-sm)", color: "var(--md-on-surface-var)" }}>%</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 8 }}>
              <Sparkline values={k.trend} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: 14, borderBottom: "1px solid var(--md-outline-var)", display: "flex", justifyContent: "space-between" }}>
            <div style={{ font: "var(--t-title-md)" }}>Rules ({rules.length})</div>
            <button style={pillBtn} onClick={() => setCreating(true)}>+ New rule</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><th style={th}>Rule</th><th style={th}>Domain</th><th style={th}>Severity</th><th style={th}>Enabled</th></tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                  <td style={td}>
                    <div>{r.name}</div>
                    <div style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{r.expression}</div>
                  </td>
                  <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{r.domainId}</td>
                  <td style={td}>
                    <span style={{ padding: "2px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                      background: r.severity === "error" ? "var(--md-error-container)" : r.severity === "warn" ? "var(--md-tertiary-container)" : "var(--md-sc-high)",
                      color: r.severity === "error" ? "var(--md-on-error-container)" : "var(--md-on-tertiary-container)" }}>
                      {r.severity}
                    </span>
                  </td>
                  <td style={td}>
                    <Switch checked={r.enabled} onChange={v => toggleRule(r, v)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: 14, borderBottom: "1px solid var(--md-outline-var)", font: "var(--t-title-md)" }}>Active violations ({violations.length})</div>
          {violations.length === 0 ? (
            <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>No active violations.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Record</th><th style={th}>Rule</th><th style={th}>Severity</th><th style={th}>Domain</th></tr></thead>
              <tbody>
                {violations.map(v => (
                  <tr key={`${v.recordId}-${v.ruleId}`} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                    <td style={{ ...td, font: "var(--t-mono-sm)" }}>{v.recordId}</td>
                    <td style={td}>{v.ruleName}</td>
                    <td style={td}>
                      <span style={{ padding: "2px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                        background: v.severity === "error" ? "var(--md-error-container)" : "var(--md-tertiary-container)",
                        color: v.severity === "error" ? "var(--md-on-error-container)" : "var(--md-on-tertiary-container)" }}>
                        {v.severity}
                      </span>
                    </td>
                    <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{v.domainId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && (
        <div style={scrim} onClick={() => setCreating(false)} role="dialog" aria-modal="true">
          <div style={dialog} onClick={e => e.stopPropagation()}>
            <div style={eyebrow}>NEW RULE</div>
            <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400, marginBottom: 12 }}>Create quality rule</div>
            <label style={{ display: "grid", gap: 4, marginBottom: 10 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Name</span>
              <input style={input} value={newRule.name} onChange={e => setNewRule({ ...newRule, name: e.target.value })} placeholder="ISIN checksum valid" />
            </label>
            <label style={{ display: "grid", gap: 4, marginBottom: 10 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Domain</span>
              <select style={input} value={newRule.domainId} onChange={e => setNewRule({ ...newRule, domainId: e.target.value })}>
                {DOMAINS.map(d => <option key={d}>{d}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, marginBottom: 10 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Expression</span>
              <input style={input} value={newRule.expression} onChange={e => setNewRule({ ...newRule, expression: e.target.value })} placeholder="len(isin)=12" />
            </label>
            <label style={{ display: "grid", gap: 4, marginBottom: 10 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Severity</span>
              <select style={input} value={newRule.severity} onChange={e => setNewRule({ ...newRule, severity: e.target.value as "info" | "warn" | "error" })}>
                <option value="info">info</option>
                <option value="warn">warn</option>
                <option value="error">error</option>
              </select>
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <button onClick={() => setCreating(false)} style={pillBtn}>Cancel</button>
              <button onClick={submitNewRule} disabled={!newRule.name || !newRule.expression} style={filledBtn}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const w = 80, h = 28;
  if (!values?.length) return <svg width={w} height={h} aria-hidden="true" />;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} aria-hidden="true">
      <polyline points={pts} fill="none" stroke="var(--md-primary)" strokeWidth={1.5} />
    </svg>
  );
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} aria-pressed={checked} style={{
      width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer", padding: 2,
      background: checked ? "var(--md-primary)" : "var(--md-outline-var)",
      display: "flex", alignItems: "center", justifyContent: checked ? "flex-end" : "flex-start",
    }}>
      <span style={{ width: 16, height: 16, borderRadius: 999, background: "var(--md-sc-lowest)" }} />
    </button>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)" };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const pillBtn: CSSProperties = { padding: "6px 14px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };
const filledBtn: CSSProperties = { padding: "10px 20px", borderRadius: "var(--r-pill)", border: "none", background: "var(--md-primary)", color: "var(--md-on-primary)", cursor: "pointer", font: "var(--t-label-lg)" };
const th: CSSProperties = { textAlign: "left", padding: "10px 14px", font: "var(--t-label-md)", color: "var(--md-on-surface-var)" };
const td: CSSProperties = { padding: "10px 14px", font: "var(--t-body-md)" };
const input: CSSProperties = { width: "100%", padding: "8px 12px", borderRadius: "var(--r-md)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)" };
const scrim: CSSProperties = { position: "fixed", inset: 0, background: "var(--md-scrim)", display: "grid", placeItems: "center", zIndex: 100 };
const dialog: CSSProperties = { width: 480, maxWidth: "90vw", background: "var(--md-sc-lowest)", padding: 24, borderRadius: "var(--r-xl)", boxShadow: "var(--e-4)" };

export default Quality;
